/**
 * Panel 2: Tempo Alerts — Real-time tempo shift feed with filtering,
 * clickthrough navigation, sparkline history, and acknowledgment workflow.
 */

import { useState, useEffect, useCallback, useMemo } from 'react'
import type { TempoAlert } from '../../../types'
import { TEMPO_CONFIG, type Tempo, THREAT_CONFIG, type ThreatLevel } from '../../../types'
import { getTempoAlerts, timeAgo } from '../../../services/api'

// ===== Props =====
interface TempoAlertsPanelProps {
  onSelectAsset: (assetId: string) => void
}

// ===== Filter types =====
type TempoFilter = 'ALL' | Tempo
type ThreatFilter = 'ALL' | ThreatLevel

const TEMPO_FILTERS: { key: TempoFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'SURGE', label: 'Surge' },
  { key: 'ATTRITION', label: 'Attrition' },
  { key: 'POSTURE_SHIFT', label: 'Posture' },
  { key: 'RECONSTITUTION', label: 'Recon' },
  { key: 'DISTRIBUTED_OPS', label: 'Distributed' },
  { key: 'STEADY_STATE', label: 'Steady' },
]

const THREAT_FILTERS: { key: ThreatFilter; label: string }[] = [
  { key: 'ALL', label: 'All' },
  { key: 'BLACK', label: 'Black' },
  { key: 'RED', label: 'Red' },
  { key: 'AMBER', label: 'Amber' },
  { key: 'GREEN', label: 'Green' },
]

// ===== Tempo Sparkline Component =====
function TempoSparkline({ trail }: { trail: string[] }) {
  if (!trail || trail.length === 0) return null

  return (
    <div className="flex items-center gap-1" title={trail.join(' → ')}>
      <span className="text-[9px] text-term-textDim mr-1 uppercase tracking-wider">Trail:</span>
      {trail.map((tempo, i) => {
        const cfg = TEMPO_CONFIG[tempo as Tempo]
        const isLast = i === trail.length - 1
        return (
          <div key={i} className="flex items-center gap-0.5">
            <div
              className={`rounded-full transition-all ${isLast ? 'w-2.5 h-2.5 ring-1 ring-white/20' : 'w-2 h-2'}`}
              style={{ backgroundColor: cfg?.color ?? '#6366f1' }}
              title={`${tempo} ${isLast ? '(current)' : ''}`}
            />
            {i < trail.length - 1 && (
              <span className="text-term-textDim/40 text-[8px]">›</span>
            )}
          </div>
        )
      })}
    </div>
  )
}

// ===== Main Panel =====
export function TempoAlertsPanel({ onSelectAsset }: TempoAlertsPanelProps) {
  const [alerts, setAlerts] = useState<TempoAlert[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // Filters
  const [tempoFilter, setTempoFilter] = useState<TempoFilter>('ALL')
  const [threatFilter, setThreatFilter] = useState<ThreatFilter>('ALL')

  // Acknowledged alerts (local state only)
  const [acknowledged, setAcknowledged] = useState<Set<string>>(new Set())

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const res = await getTempoAlerts()
      if (res.error) throw new Error(res.error)
      if (res.data) {
        // Sort newest-first
        const sorted = [...res.data].sort(
          (a, b) => new Date(b.timestamp).getTime() - new Date(a.timestamp).getTime()
        )
        setAlerts(sorted)
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to fetch tempo alerts')
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { fetchData() }, [fetchData])

  // Filtered alerts (AND logic: both filters must match)
  const filteredAlerts = useMemo(() => {
    return alerts.filter(a => {
      const tempoMatch = tempoFilter === 'ALL' || a.new_tempo === tempoFilter
      const threatMatch = threatFilter === 'ALL' || a.threat_level === threatFilter
      return tempoMatch && threatMatch
    })
  }, [alerts, tempoFilter, threatFilter])

  // Toggle acknowledgment
  const toggleAck = useCallback((alertId: string) => {
    setAcknowledged(prev => {
      const next = new Set(prev)
      if (next.has(alertId)) {
        next.delete(alertId)
      } else {
        next.add(alertId)
      }
      return next
    })
  }, [])

  // Threat level summary counts (on full dataset, not filtered)
  const threatCounts = useMemo(() => {
    return alerts.reduce((acc, a) => {
      acc[a.threat_level] = (acc[a.threat_level] || 0) + 1
      return acc
    }, {} as Record<string, number>)
  }, [alerts])

  // Loading state
  if (loading) {
    return (
      <div className="flex flex-col gap-3 animate-pulse">
        {[1,2,3,4,5].map(i => (
          <div key={i} className="h-24 bg-term-bgLight rounded border border-term-border/30" />
        ))}
      </div>
    )
  }

  // Error state
  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-full gap-4">
        <div className="text-term-error text-lg font-semibold">Error loading tempo alerts</div>
        <div className="text-term-textDim text-sm">{error}</div>
        <button onClick={fetchData} className="term-button term-button-primary">Retry</button>
      </div>
    )
  }

  return (
    <div className="flex flex-col gap-3 h-full">
      {/* ── Header: Summary + Threat Badges ── */}
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="text-xs text-term-textDim uppercase tracking-wider font-medium">
          Tempo Shift Alerts
        </div>
        <div className="flex gap-2">
          {(['BLACK', 'RED', 'AMBER', 'GREEN'] as ThreatLevel[]).map(level => {
            const count = threatCounts[level] || 0
            if (count === 0) return null
            const cfg = THREAT_CONFIG[level]
            return (
              <span key={level} className={`${cfg.cssClass} px-2 py-0.5 rounded text-[10px] font-medium`}>
                {level}: {count}
              </span>
            )
          })}
        </div>
      </div>

      {/* ── Filter Controls ── */}
      <div className="flex flex-col gap-2 border border-term-border/30 rounded p-2.5 bg-term-bgLight/30">
        {/* Tempo Type Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-term-textDim uppercase tracking-wider font-medium w-14 shrink-0">
            Tempo
          </span>
          <div className="flex gap-1 flex-wrap">
            {TEMPO_FILTERS.map(f => {
              const isActive = tempoFilter === f.key
              const tempoCfg = f.key !== 'ALL' ? TEMPO_CONFIG[f.key as Tempo] : null
              return (
                <button
                  key={f.key}
                  onClick={() => setTempoFilter(f.key)}
                  className={`
                    px-2.5 py-1 rounded text-[10px] font-medium transition-all border
                    ${isActive
                      ? tempoCfg
                        ? `${tempoCfg.cssClass} ring-1 ring-white/20`
                        : 'bg-term-text/20 text-term-text border-term-text/30 ring-1 ring-white/20'
                      : 'bg-term-bgDark/50 text-term-textDim border-term-border/20 hover:bg-term-bgLight hover:text-term-text'
                    }
                  `}
                >
                  {f.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Threat Level Filter */}
        <div className="flex items-center gap-2 flex-wrap">
          <span className="text-[10px] text-term-textDim uppercase tracking-wider font-medium w-14 shrink-0">
            Threat
          </span>
          <div className="flex gap-1 flex-wrap">
            {THREAT_FILTERS.map(f => {
              const isActive = threatFilter === f.key
              const tCfg = f.key !== 'ALL' ? THREAT_CONFIG[f.key as ThreatLevel] : null
              return (
                <button
                  key={f.key}
                  onClick={() => setThreatFilter(f.key)}
                  className={`
                    px-2.5 py-1 rounded text-[10px] font-medium transition-all border
                    ${isActive
                      ? tCfg
                        ? `${tCfg.cssClass} ring-1 ring-white/20`
                        : 'bg-term-text/20 text-term-text border-term-text/30 ring-1 ring-white/20'
                      : 'bg-term-bgDark/50 text-term-textDim border-term-border/20 hover:bg-term-bgLight hover:text-term-text'
                    }
                  `}
                >
                  {f.label}
                </button>
              )
            })}
          </div>
        </div>

        {/* Filtered count */}
        <div className="text-[10px] text-term-textDim">
          Showing {filteredAlerts.length} of {alerts.length} alerts
          {tempoFilter !== 'ALL' && (
            <span className="ml-1">
              | Tempo: <span className="text-term-text font-medium">{tempoFilter}</span>
            </span>
          )}
          {threatFilter !== 'ALL' && (
            <span className="ml-1">
              | Threat: <span className="text-term-text font-medium">{threatFilter}</span>
            </span>
          )}
        </div>
      </div>

      {/* ── Alert Feed ── */}
      <div className="flex flex-col gap-2 overflow-auto flex-1 min-h-0">
        {filteredAlerts.length === 0 ? (
          <div className="flex items-center justify-center py-12 text-term-textDim text-sm">
            No alerts match the current filters.
          </div>
        ) : (
          filteredAlerts.map(alert => {
            const prevCfg = TEMPO_CONFIG[alert.previous_tempo as Tempo]
            const newCfg = TEMPO_CONFIG[alert.new_tempo as Tempo]
            const threatCfg = THREAT_CONFIG[alert.threat_level as ThreatLevel]
            const isAcked = acknowledged.has(alert.id)

            return (
              <div
                key={alert.id}
                className={`term-box p-3 flex flex-col gap-2 transition-all duration-200 ${
                  isAcked ? 'opacity-40' : ''
                }`}
              >
                {/* Row 1: ID, Asset Name (clickable), Threat Badge, Time, Ack Toggle */}
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <span className="text-term-textDim text-xs font-mono">{alert.id}</span>
                    <button
                      onClick={() => onSelectAsset(alert.asset_id)}
                      className="text-term-text font-bold text-sm hover:underline hover:text-blue-400 transition-colors cursor-pointer"
                      title={`View readiness details for ${alert.asset_type}`}
                    >
                      {alert.asset_type}
                    </button>
                  </div>
                  <div className="flex items-center gap-2">
                    {threatCfg && (
                      <span className={`${threatCfg.cssClass} px-2 py-0.5 rounded text-[10px] font-medium`}>
                        {alert.threat_level}
                      </span>
                    )}
                    <span className="text-term-textDim text-xs">{timeAgo(alert.timestamp)}</span>
                    <button
                      onClick={() => toggleAck(alert.id)}
                      className={`
                        px-2 py-0.5 rounded text-[10px] font-medium transition-all border
                        ${isAcked
                          ? 'bg-emerald-500/30 text-emerald-300 border-emerald-400/50 ring-1 ring-emerald-400/30'
                          : 'bg-term-bgDark/50 text-term-textDim border-term-border/30 hover:bg-term-bgLight hover:text-term-text'
                        }
                      `}
                      title={isAcked ? 'Mark as unacknowledged' : 'Acknowledge alert'}
                    >
                      {isAcked ? '\u2713 ACKED' : 'ACK'}
                    </button>
                  </div>
                </div>

                {/* Row 2: Tempo Transition + Confidence */}
                <div className="flex items-center gap-2 text-sm">
                  <span className={`${prevCfg?.cssClass ?? 'tempo-steady'} px-2 py-0.5 rounded text-[10px] font-medium`}>
                    {alert.previous_tempo}
                  </span>
                  <span className="text-term-textDim">→</span>
                  <span className={`${newCfg?.cssClass ?? 'tempo-steady'} px-2 py-0.5 rounded text-[10px] font-medium`}>
                    {alert.new_tempo}
                  </span>
                  <span className="text-term-textDim text-xs ml-2">
                    Confidence: {(alert.confidence * 100).toFixed(1)}%
                  </span>
                </div>

                {/* Row 3: Confidence bar */}
                <div className="flex items-center gap-2">
                  <div className="flex-1 h-1.5 bg-term-bgDark rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-300"
                      style={{
                        width: `${alert.confidence * 100}%`,
                        backgroundColor: newCfg?.color ?? '#6366f1',
                      }}
                    />
                  </div>
                </div>

                {/* Row 4: Tempo Trail Sparkline */}
                {alert.tempo_trail && alert.tempo_trail.length > 0 && (
                  <TempoSparkline trail={alert.tempo_trail} />
                )}

                {/* Row 5: Drivers */}
                <div className="flex flex-wrap gap-1.5">
                  {alert.drivers.map((d, i) => (
                    <span
                      key={i}
                      className="text-[10px] text-term-textDim bg-term-bgLight px-2 py-0.5 rounded border border-term-border/20"
                      title={d}
                    >
                      {d}
                    </span>
                  ))}
                </div>
              </div>
            )
          })
        )}
      </div>
    </div>
  )
}
